import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authGet, authPatch, authDelete } from '../../utils/auth';
import useRequireAdmin from '../../hooks/useRequireAdmin';
import {
  ArrowLeft, Mail, MailOpen, Reply, Archive, Trash2,
  RefreshCw, Eye, Clock, Building2, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const ContactManagement = () => {
  const navigate = useNavigate();
  // Server-verified admin gate.
  const { user, checking } = useRequireAdmin();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [stats, setStats] = useState({ total: 0, new_count: 0 });
  const [filter, setFilter] = useState('all');

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await authGet(`/admin/contacts${params}`);
      setSubmissions(response.data.submissions || []);
      setStats({
        total: response.data.total,
        new_count: response.data.new_count
      });
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contact submissions. Please try again.');
    }
    setLoading(false);
  }, [filter]);

  // Only fetch once the admin session is verified; re-runs on filter change.
  useEffect(() => {
    if (user) fetchSubmissions();
  }, [user, fetchSubmissions]);

  const viewSubmission = async (submission) => {
    try {
      const response = await authGet(`/admin/contacts/${submission.id}`);
      const full = response.data;
      setSelectedSubmission(full);
      setDetailDialog(true);
      // Viewing marks it read server-side — update just this row + the counter
      // instead of refetching the whole list.
      setSubmissions(prev => prev.map(s =>
        s.id === submission.id ? { ...s, status: full.status || s.status } : s
      ));
      if (submission.status === 'new') {
        setStats(prev => ({ ...prev, new_count: Math.max(0, prev.new_count - 1) }));
      }
    } catch (error) {
      toast.error('Failed to load submission');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await authPatch(`/admin/contacts/${id}`, { status });
      toast.success(`Marked as ${status}`);
      // Update the changed row locally rather than refetching the whole list.
      setSubmissions(prev => prev.map(s => (s.id === id ? { ...s, status } : s)));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this submission?')) return;

    try {
      await authDelete(`/admin/contacts/${id}`);
      toast.success('Submission deleted');
      setDetailDialog(false);
      // Drop the row locally instead of refetching the whole list. If the
      // deleted submission was still "new", decrement the new counter too so
      // the badge/header don't stay inflated.
      const wasNew = submissions.find(s => s.id === id)?.status === 'new';
      setSubmissions(prev => prev.filter(s => s.id !== id));
      setStats(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        new_count: wasNew ? Math.max(0, prev.new_count - 1) : prev.new_count,
      }));
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'read': return 'bg-muted text-muted-foreground';
      case 'replied': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'archived': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (checking || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-muted rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary" />
                Contact Submissions
              </h1>
              <p className="text-sm text-muted-foreground">
                {stats.new_count > 0 && <span className="text-blue-600 font-medium">{stats.new_count} new</span>}
                {stats.new_count > 0 && ' • '}
                {stats.total} total
              </p>
            </div>
          </div>
          
          <Button variant="outline" onClick={fetchSubmissions}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['all', 'new', 'read', 'replied', 'archived'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f 
                  ? 'bg-primary text-white' 
                  : 'bg-card border border-border hover:bg-muted'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'new' && stats.new_count > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                  {stats.new_count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg p-4 text-sm flex items-center justify-between gap-4">
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={fetchSubmissions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        )}

        {/* Submissions List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {submissions.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No contact submissions found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {submissions.map(submission => (
                <div
                  key={submission.id}
                  className={`p-4 hover:bg-muted/30 cursor-pointer transition-colors ${
                    submission.status === 'new' ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                  onClick={() => viewSubmission(submission)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {submission.status === 'new' ? (
                        <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <MailOpen className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{submission.name}</span>
                          <span className="text-sm text-muted-foreground">&lt;{submission.email}&gt;</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(submission.status)}`}>
                            {submission.status}
                          </span>
                        </div>
                        <p className="font-medium text-sm mt-1">{submission.subject}</p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {submission.message}
                        </p>
                        {submission.business_name && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {submission.business_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground flex-shrink-0">
                      {formatDate(submission.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Info */}
        <div className="mt-8 bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-heading font-semibold mb-4">API Endpoints</h2>
          <div className="space-y-3 font-mono text-sm">
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs">POST</span>
              <span className="text-muted-foreground">/api/contact</span>
              <span className="text-xs text-muted-foreground">- Submit contact form</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">GET</span>
              <span className="text-muted-foreground">/api/v1/contact/info</span>
              <span className="text-xs text-muted-foreground">- Get public contact info</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">GET</span>
              <span className="text-muted-foreground">/api/admin/contacts</span>
              <span className="text-xs text-muted-foreground">- List submissions (admin)</span>
            </div>
          </div>
        </div>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Contact Submission</DialogTitle>
            </DialogHeader>
            
            {selectedSubmission && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={`text-sm px-3 py-1 rounded-full ${getStatusColor(selectedSubmission.status)}`}>
                    {selectedSubmission.status}
                  </span>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(selectedSubmission.created_at)}
                  </span>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">From</label>
                    <p className="font-medium flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      {selectedSubmission.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Email</label>
                    <p className="font-medium">
                      <a href={`mailto:${selectedSubmission.email}`} className="text-primary hover:underline">
                        {selectedSubmission.email}
                      </a>
                    </p>
                  </div>
                </div>
                
                {selectedSubmission.business_name && (
                  <div>
                    <label className="text-xs text-muted-foreground">Business</label>
                    <p className="font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {selectedSubmission.business_name}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-muted-foreground">Subject</label>
                  <p className="font-semibold text-lg">{selectedSubmission.subject}</p>
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Message</label>
                  <div className="mt-1 p-4 bg-muted/50 rounded-lg whitespace-pre-wrap">
                    {selectedSubmission.message}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedSubmission.id, 'replied')}
                  >
                    <Reply className="w-4 h-4 mr-1" />
                    Mark Replied
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedSubmission.id, 'archived')}
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    Archive
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteSubmission(selectedSubmission.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ContactManagement;
